import { Component, Inject, OnInit } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { AutomataOperationsService, RuleInterface } from '@vendasta/automata';
import { combineLatest, Observable } from 'rxjs';
import { switchMap, take, tap } from 'rxjs/operators';
import { AutomationVariableMenuComponent } from '../../../../../common/components/automation-variable-menu/automation-variable-menu.component';
import { insertVariable } from '../../../../../common/components/automation-variable-menu/automation-variable-menu.service';
import { AutomationViewService } from '../../../../../automation/automation-view.service';
import { AUTOMATION_NAMESPACE_INJECTION_TOKEN$, CurrentAutomationService } from '@galaxy/automata/shared';
import { ALL_VARIABLE_MENU_DATA_TYPES } from '../../../../../common/constants';
import { CommonActionPanelComponent } from '../../../common/common-action-panel-component.directive';
import { secureUrlValidator, urlValidator } from '../../../common/utils/url-validator';
import { getEntityIDReplacementString } from '../../../../../shared/entities';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';

export interface AdditionalFields {
  [key: string]: string;
}

@Component({
  selector: 'automata-call-endpoint-panel',
  templateUrl: './call-endpoint-panel.component.html',
  styleUrls: ['./call-endpoint-panel.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    AutomationVariableMenuComponent,
  ],
})
export class CallEndpointPanelComponent extends CommonActionPanelComponent implements OnInit {
  form: UntypedFormGroup;
  loadingForm = true;
  submitting = false;
  allDataTypes = ALL_VARIABLE_MENU_DATA_TYPES;

  constructor(
    @Inject(AUTOMATION_NAMESPACE_INJECTION_TOKEN$) public readonly namespace$: Observable<string>,
    protected readonly automationViewService: AutomationViewService,
    readonly automataOperationsService: AutomataOperationsService,
    protected readonly currentAutomationService: CurrentAutomationService,
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    const jsonData = this.step ? JSON.parse(this.step.data) : {};

    this.form = new UntypedFormGroup({
      endpoint_url: new UntypedFormControl(jsonData.endpoint_url?.replace('https://', '') || '', [
        urlValidator,
        secureUrlValidator,
      ]),
      verifier_token: new UntypedFormControl(jsonData.verifier_token || ''),
      additional_fields: new UntypedFormArray([]),
    });
    this.populateAdditionalFields(jsonData.additional_fields);
    this.automationViewService.isNonPartnerAutomation().pipe(
      take(1),
      tap((isNonPartnerAutomation) => {
        if (!isNonPartnerAutomation) {
          this.form.controls.endpoint_url.setValidators(Validators.required);
        }
      }),
    );

    // Since there's nothing asynchronous about this form, saving is enabled on initialization.
    this.savingEnabled = true;
    this.loadingForm = false;
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    return [this.form];
  }

  getData(): any {
    const jsonData = this.step ? JSON.parse(this.step.data) : {};

    if (!this.form) {
      return jsonData;
    }
    if (this.form.controls.endpoint_url.value) {
      jsonData.endpoint_url = this.ensureUrlStartsWithHttps(this.form.controls.endpoint_url.value);
    } else {
      jsonData.endpoint_url = '';
    }
    jsonData.verifier_token = this.form.controls.verifier_token.value;
    jsonData.additional_fields = this.convertToKeyValue(this.form.controls.additional_fields.value);
    return jsonData;
  }

  getRules(): RuleInterface[] {
    return [];
  }

  isFormValid(): boolean {
    return this.form.valid;
  }

  sendTestWebhook(): void {
    if (this.submitting) {
      return;
    }

    this.submitting = true;

    combineLatest([this.namespace$, this.currentAutomationService.automation$])
      .pipe(
        switchMap(([partnerId, automation]) => {
          const url = this.ensureUrlStartsWithHttps(this.form.controls.endpoint_url.value);
          const fields: AdditionalFields = this.convertToKeyValue(this.form.controls.additional_fields.value);

          return this.automataOperationsService.sendTestWebhook(
            partnerId,
            '',
            automation.id,
            url,
            this.form.controls.verifier_token.value,
            fields,
          );
        }),
        take(1),
      )
      .subscribe()
      .add(() => (this.submitting = false));
  }

  getEntityIDReplacementString(): string {
    return getEntityIDReplacementString(this.automation.entityType);
  }

  removeField(i: number): void {
    (this.form.controls.additional_fields as UntypedFormArray).removeAt(i);
  }

  createField(id = '', value = ''): UntypedFormGroup {
    return new UntypedFormGroup({ id: new UntypedFormControl(id), value: new UntypedFormControl(value) });
  }

  addField(): void {
    const field = this.form.get('additional_fields') as UntypedFormArray;
    field.push(this.createField());
  }

  convertToKeyValue(fields: any): AdditionalFields {
    const additionalFields: AdditionalFields = {};
    for (const field of fields) {
      additionalFields[field.id] = field.value;
    }
    return additionalFields;
  }

  ensureUrlStartsWithHttps(url: string): string {
    if (url.startsWith('https://')) {
      return url;
    }
    return 'https://' + url;
  }

  insertVariable(elementID: string, control: any, variable: string): void {
    insertVariable(elementID, control, variable);
  }

  get additionalFieldsGetter(): any {
    return this.form.get('additional_fields')['controls'];
  }

  populateAdditionalFields(fields: AdditionalFields): void {
    const additionalFields = this.form.get('additional_fields') as UntypedFormArray;
    Object.keys(fields || {}).forEach((field) => additionalFields.push(this.createField(field, fields[field])));
  }
}
